/**
 * Phase 8 chunk 1 — single source of truth for the
 * `(prefers-reduced-motion: reduce)` user preference.
 *
 * The preference covers users with vestibular disorders or motion
 * sensitivity who configure their OS to request reduced animation.
 * `req_spec.md` §21 promised respect for this preference; nothing
 * in the SPA honored it before this chunk.
 *
 * Two consumers:
 *   1. CSS — components add `@media (prefers-reduced-motion: reduce)`
 *      blocks directly; no util call needed (the browser handles it).
 *   2. JavaScript-driven animation — RAF loops, Cesium camera flights,
 *      Lit-state-driven transforms — these import {@link prefersReducedMotion}
 *      and gate the animation accordingly.
 *
 * Subscription support is provided via {@link onMotionPreferenceChange}
 * so long-running animations (e.g. the chunk-3A dot-pulse) can
 * gracefully switch to a static state when the user flips the
 * preference at runtime without selecting a new satellite.
 *
 * Defensive against non-browser environments (Vitest's node-based
 * runner, server-side rendering): when `window.matchMedia` isn't
 * available we report "not reduced" — animations run, just like they
 * would on a browser that doesn't support the media query.
 */

const QUERY = '(prefers-reduced-motion: reduce)';

let cachedQuery: MediaQueryList | null = null;
let subscribedToChanges = false;
const listeners = new Set<(reduced: boolean) => void>();

function getMediaQueryList(): MediaQueryList | null {
  if (cachedQuery !== null) return cachedQuery;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  cachedQuery = window.matchMedia(QUERY);
  return cachedQuery;
}

function ensureChangeListener(): void {
  if (subscribedToChanges) return;
  const mq = getMediaQueryList();
  if (mq === null) return;
  // `addEventListener` is the modern API; Safari ≤14 needs the deprecated
  // `addListener` callback. Modern Cesium baseline already excludes
  // Safari 14 so we just use the standard API.
  mq.addEventListener('change', (e: MediaQueryListEvent) => {
    for (const listener of listeners) {
      listener(e.matches);
    }
  });
  subscribedToChanges = true;
}

/** True when the user has requested reduced motion at the OS level. */
export function prefersReducedMotion(): boolean {
  const mq = getMediaQueryList();
  return mq !== null && mq.matches;
}

/**
 * Subscribe to runtime changes of the reduced-motion preference.
 * Returns an unsubscribe function. The listener is *not* invoked
 * synchronously with the current value on subscribe; call
 * {@link prefersReducedMotion} for that.
 */
export function onMotionPreferenceChange(listener: (reduced: boolean) => void): () => void {
  ensureChangeListener();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Test-only: reset the cached MediaQueryList + subscription. Lets
 * Vitest cases swap in a fresh stub between tests without leaking
 * state across the suite.
 */
export function __resetMotionForTests(): void {
  cachedQuery = null;
  subscribedToChanges = false;
  listeners.clear();
}
